import type { Cam } from './camera';

/*
 * THE WALK — the fog, and what opening a name does to it.
 *
 * Ported from `reference/universe-v8/engine.js` — `recomputeFog` (256–265), `known` and `reachable`
 * (266–267), `visit` (274–283), `look` (284–289) and the map half of `warp` (800–821).
 *
 * THIS IS WHAT MAKES IT A MAP YOU UNCOVER RATHER THAN A PICTURE YOU LOOK AT. Without it every name
 * is lit the same and the field is a diagram; with it, the map is dark except where the reader has
 * been and one step beyond, and it grows as they walk. The whole fog vocabulary the layers already
 * speak — walked, seen, neither — is produced here and nowhere else.
 *
 * TWO SETS, AND THE SECOND IS DERIVED. `visited` is what the reader has opened. `seen` is
 * everything one firm tie from it — recomputed from scratch each time rather than added to,
 * because a tie can be softened or a name can arrive later and an incrementally-grown set would
 * drift from what the ties actually say.
 *
 * A SOFT TIE CARRIES NO SIGHT. Their `weak` set is skipped when the frontier is computed, so an
 * inferred connection lets the reader see nothing new. That is the whole difference between "these
 * texts say these two are connected" and "somebody guessed", and it is why the frontier can be
 * trusted as an answer to "where may I go next".
 *
 * `known` AND `reachable` ARE THE SAME PREDICATE in their engine — written twice, identically,
 * because they answer different questions that happen to have the same answer: may I read this
 * name, and may I walk to it. Kept as two, because the day they diverge the call sites should not
 * have to be found again.
 */

/** What the reader has walked, and what that puts within reach. */
export interface Fog {
  /** The names they have opened, by index. */
  visited: ReadonlySet<number>;
  /** Everything one firm tie beyond, and nothing they have already opened. */
  seen: ReadonlySet<number>;
}

/** A name, as the walk needs it. */
export interface WalkNode {
  id: number;
  x: number;
  y: number;
}

/** The unordered key a soft tie is held under. */
function pairKey(a: number, b: number): string {
  return `${Math.min(a, b)}:${Math.max(a, b)}`;
}

/**
 * Work out the whole fog from what has been opened.
 *
 * From scratch every time, and cheap enough to be: it is one pass over the opened names and their
 * own ties, not over the roster.
 */
export function recomputeFog(
  opened: ReadonlySet<number>,
  edgesOf: (id: number) => readonly number[],
  weak?: ReadonlySet<string>,
): Fog {
  const visited = new Set<number>(opened);
  const seen = new Set<number>();
  for (const id of visited) {
    for (const other of edgesOf(id)) {
      if (visited.has(other)) continue;
      /* A soft tie shows nothing: somebody inferred it, and an inference is not a road. */
      if (weak?.has(pairKey(id, other)) === true) continue;
      seen.add(other);
    }
  }
  return { visited, seen };
}

/** Whether this name may be read at all — walked, or within one step of somewhere walked. */
export function known(fog: Fog, id: number): boolean {
  return fog.visited.has(id) || fog.seen.has(id);
}

/**
 * Whether the reader may walk to this name.
 *
 * The same test as `known` in their engine and deliberately a separate function: "can I read this"
 * and "can I go here" are different questions, and the day the answers differ every call site
 * should already be asking the right one.
 */
export function reachable(fog: Fog, id: number): boolean {
  return known(fog, id);
}

/** What opening a name did: the new fog, and how much further the frontier now reaches. */
export interface Opened {
  fog: Fog;
  /** How many names came within reach that were not before. Zero on the very first name. */
  grew: number;
}

/**
 * Open a name.
 *
 * `grew` is reported rather than announced: theirs raises a message here, and a message is words,
 * which belong to the caller and to the dictionary. What this owns is the number.
 */
export function visit(
  fog: Fog,
  id: number,
  edgesOf: (id: number) => readonly number[],
  weak?: ReadonlySet<string>,
): Opened {
  const before = fog.visited.size + fog.seen.size;
  const opened = new Set(fog.visited);
  opened.add(id);
  const next = recomputeFog(opened, edgesOf, weak);
  const after = next.visited.size + next.seen.size;
  /* Their gate: nothing is announced on the first name, when everything is new by definition. */
  return { fog: next, grew: before > 0 ? after - before : 0 };
}

/**
 * WHERE THE CAMERA GOES WHEN A NAME IS OPENED.
 *
 * Their 2.3 is a zoom and therefore an argument, like every other. The `max` is the part worth
 * noticing: opening a name never pulls the reader BACK. If they were already closer than the
 * arrival zoom, they stay where they are and only the centre moves — because someone who has come
 * in to read is not asking to be taken back out.
 */
export function warpTo(node: WalkNode, from: Cam, arriveAt: number): Cam {
  return { x: node.x, y: node.y, z: Math.max(arriveAt, from.z) };
}

/**
 * WHERE THE CAMERA GOES WHEN A NAME IS OUT OF REACH.
 *
 * Not to it — toward it, and only far enough to see it. Their `min` is the mirror of `warpTo`'s
 * `max`: looking never takes the reader further IN, because the point is to show them where the
 * thing is in relation to everything else, and coming closer would lose exactly that.
 */
export function lookTo(node: WalkNode, from: Cam, lookAt: number): Cam {
  return { x: node.x, y: node.y, z: Math.min(from.z, lookAt) };
}

/*
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT THE READER HAS ACTUALLY READ.
 *
 * Two answers to that, and they are different questions. `note` is a tally kept as they go — what
 * they walked past, in their own record. `coverage` is a measure taken against the whole library —
 * how much of each scripture they have opened, out of how much there is.
 *
 * NEITHER IS A SCORE, and the distinction is the reason both exist rather than one. A tally says
 * "you have read in four scriptures"; a fraction says "you have read a thirtieth of one". The
 * second is only honest alongside the first, because a reader who has walked forty names in the
 * Hebrew Bible has done something real and 40/1099 renders it as nothing.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** A name, as the record needs it: which scriptures name it, packed as one character each. */
/**
 * A figure as the record reads it: something to identify it by, and which scriptures name it.
 *
 * GENERIC IN THE ID because it has two callers with two ways of naming a figure. The engine numbers
 * them and the app spells them out, and `coverage` cares about neither — it counts. Typing it to the
 * engine's numbers meant the app could not call it, which is how a second implementation of a tested
 * function gets written.
 */
export interface RecordedNode<Id = number> {
  id: Id;
  /** The corpus keys this figure appears in — see `our-scene.ts`'s `KEY_ALPHABET`. */
  cs: Iterable<string>;
}

/**
 * The reader's own record, accumulated as they walk.
 *
 * Immutable: theirs mutates one object hanging off the component, which means the record and the
 * thing that draws it are the same object and a redraw cannot be triggered by it changing. Ours
 * returns a new one, which is the same arithmetic and a caller that can hold state.
 */
export interface Acts {
  /** Figures they opened whose cross-scripture identity is disputed. */
  disputed: number;
  /** Passages they opened that carry the original language. */
  originals: number;
  /** Which scriptures they have touched at all. */
  corpora: ReadonlySet<string>;
}

/** Nothing read yet. */
export function noActs(): Acts {
  return { disputed: 0, originals: 0, corpora: new Set() };
}

/**
 * Add one opened name to the record.
 *
 * `disputed` and `originals` are ARGUMENTS rather than lookups: theirs reads two module-scope
 * tables keyed by the figure's NAME, and a name is not an identity — two figures can share one.
 * The caller knows which figure this is and can answer both about that figure rather than about
 * whatever else answers to its name.
 *
 * Their `orig` counts one per ORIGINAL-LANGUAGE PASSAGE, not one per figure, so a figure with
 * eleven such passages adds eleven. Kept exactly: the number means "passages read in the original",
 * and making it per-figure would quietly turn it into something else with the same label.
 */
export function note(acts: Acts, node: RecordedNode, disputed: boolean, originals: number): Acts {
  const corpora = new Set(acts.corpora);
  for (const k of node.cs) corpora.add(k);
  return {
    disputed: acts.disputed + (disputed ? 1 : 0),
    originals: acts.originals + originals,
    corpora,
  };
}

/** How much of one scripture the reader has walked. */
export interface Covered {
  /** The corpus key, as `RecordedNode.cs` packs it. */
  key: string;
  /** Names in it they have opened, and names in it at all. */
  got: number;
  tot: number;
  /** `got / tot`, and ZERO rather than NaN when a scripture has no names at all. */
  pct: number;
}

/**
 * How much of each scripture the reader has opened.
 *
 * A figure counts toward EVERY scripture that names it — Abraham is one name walked in the Hebrew
 * Bible and in the Quran both — which is why this is not simply the visited count divided up.
 *
 * `keys` is the list to report, in the order to report it, and it is also a FILTER: a corpus
 * character not in it is skipped entirely rather than reported as a scripture nobody has heard of.
 * Theirs hard-codes four; ours are eight and come from the roster, which is the whole difference
 * between a measure of this library and a measure of theirs.
 */
export function coverage<Id>(
  nodes: readonly RecordedNode<Id>[],
  visited: ReadonlySet<Id>,
  keys: readonly string[],
): Covered[] {
  const tot = new Map<string, number>();
  const got = new Map<string, number>();
  for (const key of keys) { tot.set(key, 0); got.set(key, 0); }
  for (const n of nodes) {
    for (const k of n.cs) {
      const held = tot.get(k);
      /*
       * A corpus this caller did not ask about. Theirs tests `tot[k]==null` in the same place.
       *
       * RECORDED AS DEAD, in theirs as well as ours, and kept: the answer is built by mapping over
       * `keys`, so a corpus tallied here that nobody asked about could never reach the caller
       * anyway. Every mutation of this line passes. It is the two together that make it safe —
       * remove EITHER the guard or the `keys.map` and an unasked corpus starts being reported, so
       * this is load-bearing against a future change rather than against today's inputs.
       */
      if (held === undefined) continue;
      tot.set(k, held + 1);
      if (visited.has(n.id)) got.set(k, (got.get(k) ?? 0) + 1);
    }
  }
  return keys.map(key => {
    const t = tot.get(key) ?? 0;
    const g = got.get(key) ?? 0;
    return { key, got: g, tot: t, pct: t === 0 ? 0 : g / t };
  });
}
